import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Check, Clock } from "lucide-react";
import { toast } from "sonner";

/**
 * AccessCodeModal — PROMPT 014
 *
 * A focused dialog that shows a free-inbox access code together with the
 * SAME live countdown the header already renders. The countdown value is
 * passed in via `timeRemaining` (the parent's already-running interval) —
 * we deliberately do NOT spin up another `setInterval` here so the modal's
 * remaining-time always matches what the user sees in the toolbar.
 *
 * Props:
 *   open          : boolean
 *   onOpenChange  : (open: boolean) => void
 *   accessCode    : string             - the v-XXXX code, plain
 *   timeRemaining : {text, urgent, expired, ...} | null
 *                  Same shape produced by FreeAliasPage's `setTimeRemaining`.
 *   onInvite      : () => void         - optional: opens the existing
 *                                        invite/share UI from the parent.
 */
export default function AccessCodeModal({
  open, onOpenChange, accessCode, timeRemaining, onInvite,
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!accessCode) return;
    try {
      await navigator.clipboard.writeText(accessCode);
      setCopied(true);
      toast.success("Access code copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy code");
    }
  };

  const remainingText = timeRemaining?.expired
    ? "Expired"
    : (timeRemaining?.text || "—");
  const urgent = !!timeRemaining?.urgent;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md w-[calc(100vw-2rem)] sm:w-full max-h-[calc(100vh-2rem)] overflow-y-auto"
        data-testid="access-code-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Access code</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Live countdown — shares state with the header timer */}
          <div
            className={`flex items-center gap-2 rounded-md px-3 py-2 border ${
              urgent
                ? "bg-red-50 border-red-100 text-red-700"
                : "bg-[#F5F5F5] border-[#E5E5E5] text-[#333333]"
            }`}
            data-testid="access-code-countdown"
          >
            <Clock size={14} className={urgent ? "text-red-600" : "text-zinc-500"} />
            <span className="text-xs">Time remaining:</span>
            <span
              className={`text-sm font-mono tabular-nums font-semibold ${
                urgent ? "text-red-700" : "text-[#111111]"
              }`}
              data-testid="access-code-countdown-value"
            >
              {remainingText}
            </span>
          </div>

          <p className="text-sm text-[#555555] leading-snug">
            Save this code to access your inbox again within this time.
          </p>

          {/* Code box */}
          <div
            className="rounded-lg bg-[#F5F5F5] border border-[#E5E5E5] px-4 py-3 flex items-center gap-3"
            data-testid="access-code-box"
          >
            <code
              className="flex-1 font-mono text-base sm:text-lg tracking-wide text-[#111111] select-all break-all"
              data-testid="access-code-value"
            >
              {accessCode || "—"}
            </code>
          </div>

          {onInvite && (
            <button
              type="button"
              onClick={onInvite}
              className="text-xs text-blue-600 hover:text-blue-800 underline-offset-2 hover:underline"
              data-testid="access-code-invite-link"
            >
              Invite a friend to extend your time.
            </button>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2 flex-row justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="access-code-close-button"
          >
            Close
          </Button>
          <Button
            onClick={handleCopy}
            disabled={!accessCode}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="access-code-copy-button"
          >
            {copied ? <Check size={14} className="mr-1.5" /> : <Copy size={14} className="mr-1.5" />}
            {copied ? "Copied" : "Copy code"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
